import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Link, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type { AnnotatedBillingLine, BillingLineCapabilities } from './billingLineSource';
import { useBillingLineActions } from './billingLineActions';
import { displayGrossCents, eurosToCents } from '@/utils/money';

const READONLY_CAPS: BillingLineCapabilities = {
  quantity: false,
  unitPrice: false,
  discount: false,
  deletable: false,
};

function formatNumber(value: number): string {
  return (Math.round(value * 100) / 100).toString().replace('.', ',');
}

function parseNonNegativeDecimal(raw: string): number | null {
  const parsed = Number.parseFloat(raw.replace(',', '.').trim());
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

interface DraftFieldProps {
  committed: string;
  label: string;
  testId: string;
  disabled: boolean;
  onCommit: (raw: string) => string | null;
  /**
   * Rendered inside the field rather than beside it. A sibling button takes its
   * width out of the row, so the three fields would stop being equal thirds the
   * moment it appeared (#2209).
   */
  endAdornment?: ReactNode;
}

function DraftField({
  committed,
  label,
  testId,
  disabled,
  onCommit,
  endAdornment,
}: DraftFieldProps) {
  const [draft, setDraft] = useState(committed);
  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  return (
    <TextField
      size="small"
      label={label}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9.,]/g, ''))}
      onBlur={() => {
        const next = onCommit(draft);
        if (next == null) {
          setDraft(committed);
          return;
        }
        setDraft(next);
      }}
      sx={{ flex: 1, minWidth: 0 }}
      slotProps={{
        htmlInput: { 'data-testid': testId, inputMode: 'decimal' },
        ...(endAdornment ? { input: { endAdornment } } : {}),
      }}
    />
  );
}

export interface BillingLineRowProps {
  line: AnnotatedBillingLine;
  formatPrice: (cents: number) => string;
  /**
   * Gross cents already allocated for this line (#2642). The row shows this
   * figure so displayed totals sum to the group total to the cent.
   */
  lineGrossCents: number;
  fallbackTaxPercent: number;
  unitLabel: string;
  editable: boolean;
  saving: boolean;
  onCommitQuantity: (line: AnnotatedBillingLine, quantity: number) => void;
  onCommitUnitPrice: (line: AnnotatedBillingLine, unitPriceGrossCents: number) => void;
  onCommitDiscount: (line: AnnotatedBillingLine, discountPercent: number) => void;
  onDelete: (line: AnnotatedBillingLine) => void;
}

export function BillingLineRow({
  line,
  formatPrice,
  lineGrossCents,
  fallbackTaxPercent,
  unitLabel,
  editable,
  saving,
  onCommitQuantity,
  onCommitUnitPrice,
  onCommitDiscount,
  onDelete,
}: BillingLineRowProps) {
  const { t: ti } = useTranslation('invoices');
  const { t } = useTranslation('workorders');
  const { resetFixedServicePrice, openServiceLabor } = useBillingLineActions();
  const caps = editable ? (line.source?.capabilities ?? READONLY_CAPS) : READONLY_CAPS;
  const showEditor = editable && (caps.quantity || caps.unitPrice || caps.discount || caps.deletable);
  const overrideActive = line.source?.kind === 'fixedService' && line.source.overrideActive;
  // The combined labour line is shown as invoiced but edited per step in the
  // Works card (#2638); say so where the user would otherwise try to edit it.
  const serviceLabor = editable && line.source?.kind === 'serviceLabor' ? line.source : null;
  const unitPriceGrossCents = displayGrossCents(
    line.unitPriceCents, line.taxable, line.taxPercent, fallbackTaxPercent,
  );
  const discount = line.discountPercent > 0
    ? ` · ${ti('print.discount')} ${formatNumber(line.discountPercent)} %`
    : '';
  const vat = line.taxable
    ? `${formatNumber(line.taxPercent ?? fallbackTaxPercent)} %`
    : '—';

  return (
    <Box
      data-testid="invoice-grouped-line"
      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          {line.description}
        </Typography>
        {overrideActive && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="billing-fixed-price-override"
          >
            {t('detail.fixedPriceOverridden')}
          </Typography>
        )}
        {showEditor ? (
          <Box sx={{ mt: 0.75 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {caps.quantity ? (
              <DraftField
                committed={formatNumber(line.quantity)}
                label={ti('print.quantity')}
                testId="billing-line-quantity"
                disabled={saving}
                onCommit={(raw) => {
                  const parsed = parseNonNegativeDecimal(raw);
                  if (parsed == null) return null;
                  if (parsed === line.quantity) return formatNumber(parsed);
                  onCommitQuantity(line, parsed);
                  return formatNumber(parsed);
                }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                {formatNumber(line.quantity)} {unitLabel}
              </Typography>
            )}
            {caps.unitPrice ? (
              <>
                <DraftField
                  committed={(unitPriceGrossCents / 100).toFixed(2)}
                  label={ti('edit.unitPriceInclVat')}
                  testId="billing-line-unit-price"
                  disabled={saving}
                  onCommit={(raw) => {
                    const cents = eurosToCents(raw.replace(',', '.'));
                    if (cents == null) return null;
                    const normalized = (cents / 100).toFixed(2);
                    if (cents === unitPriceGrossCents) return normalized;
                    onCommitUnitPrice(line, cents);
                    return normalized;
                  }}
                  {...(overrideActive
                    ? {
                        endAdornment: (
                          <Tooltip title={t('detail.resetToCatalogPrice')}>
                            <span>
                              <IconButton
                                size="small"
                                edge="end"
                                onClick={() => resetFixedServicePrice(line)}
                                disabled={saving}
                                aria-label={t('detail.resetToCatalogPrice')}
                                data-testid="billing-line-reset-catalog"
                              >
                                <RestartAltIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ),
                      }
                    : {})}
                />
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                × {formatPrice(unitPriceGrossCents)}
              </Typography>
            )}
            {caps.discount ? (
              <DraftField
                committed={formatNumber(line.discountPercent)}
                label={ti('print.lineDiscountHeader')}
                testId="billing-line-discount"
                disabled={saving}
                onCommit={(raw) => {
                  const parsed = parseNonNegativeDecimal(raw);
                  if (parsed == null || parsed > 100) return null;
                  if (parsed === line.discountPercent) return formatNumber(parsed);
                  onCommitDiscount(line, parsed);
                  return formatNumber(parsed);
                }}
              />
            ) : discount ? (
              <Typography variant="caption" color="text.secondary">{discount}</Typography>
            ) : null}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {ti('print.lineVat')} {vat}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {formatNumber(line.quantity)} {unitLabel} × {formatPrice(unitPriceGrossCents)}
            {discount} · {ti('print.lineVat')} {vat}
          </Typography>
        )}
        {serviceLabor && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mt: 0.5 }}
            data-testid="billing-combined-labor-hint"
          >
            {ti('workOrderBilling.combinedLaborHint', { count: serviceLabor.laborItemIds.length })}
            {openServiceLabor && (
              <>
                {' '}
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => openServiceLabor(serviceLabor.bookingServiceId)}
                  data-testid="billing-combined-labor-open"
                  sx={{ verticalAlign: 'baseline' }}
                >
                  {ti('workOrderBilling.combinedLaborOpen')}
                </Link>
              </>
            )}
          </Typography>
        )}
      </Box>
      {/* The totals column sizes to its own content, so a longer sum used to
          steal width from the fields beside it and knock that row's editor out
          of line with the rows above (#2209). A floor plus right alignment keeps
          every row's fields on the same grid, whatever the amounts are. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          minWidth: 108,
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          data-testid="invoice-grouped-line-total"
          data-gross-cents={lineGrossCents}
        >
          {formatPrice(lineGrossCents)}
        </Typography>
        {caps.deletable && (
          <Tooltip title={ti('edit.removeLine')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDelete(line)}
                disabled={saving}
                aria-label={ti('edit.removeLine')}
                data-testid="billing-line-delete"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
